import React from "react";
import {
    ColumnLayout,
    Container,
    Header,
    Alert,
    SpaceBetween,
    Button,
    Modal,
} from "@amzn/awsui-components-react";
import {Widget} from "../Component/Widget";
import {SingletonServiceClientBuilder} from '../APIs';
import {SUBMIT_SUCCESS_TIP} from '../common/strings';
import PubSub from 'pubsub-js';

const productInfoFields = [
    "listingId",
    "listingIdUrl",
    'productTitle',
    "sku",
    'logoUrl',
    "logoUrl",
    "videoUrl",
    "promotionalMedia",
    "shortDescription",
    "longDescription",
    "highlights",
    "supportDetail",
    "categories",
    "searchKeywords",
    "fulfillmentUrl",
    "taxRate",
    "taxRateDesc",
    "refundPolicy",
    "legalTerm",
];

const reviewMaterialsFields = [
    'supportingDocuments',
    'role',
    'resellerAuthorizationLetters',
    'sellerEmail',
    'sellerTelephone',
];

const legalReviewFields = [
    'legalReviewDecision',
    'legalReviewComment'
];

const mcoReviewFields = [
    'mcoReviewDecision',
    'mcoReviewComment'
];

export class SSLV2DetailReviewWorkflow extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedNodeId: '',
            widgetListPerNode: {},
            currentActiveNode: null,
            workflowStatus: '',
            isSubmitNodes: {},
            submittedNodes: {},
            alertVisible: false,
            alertMsg: "",
            modalVisible: false,
            modalContent: ""
        };

        this.client = SingletonServiceClientBuilder.getClient();
        this.setWidgetValue = this.setWidgetValue.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.validateWidget = this.validateWidget.bind(this);
        this.restructureWidget = this.restructureWidget.bind(this);
        this.switchToNode = this.switchToNode.bind(this);
    }

    componentDidMount() {
        this.initializeWorkflow();
        PubSub.subscribe('updateWorkflow', () => {
            this.initializeWorkflow();
        });
    }

    componentWillUnmount() {
        PubSub.unsubscribe('updateWorkflow');
    }

    initializeWorkflow() {
        const {details} = this.props;
        if (!details || !details.nodeList) return;

        this.setState({workflowStatus: details.status});

        const completedNodes = {};
        details.nodeList.forEach(node => {
            if (node.status === 'SUCCEEDED' || node.status === 'FAILED' || node.status === 'COMPLETED') {
                completedNodes[node.id] = true;
            }
        });

        this.setState(prevState => ({
            submittedNodes: {
                ...prevState.submittedNodes,
                ...completedNodes
            }
        }));

        details.nodeList.forEach(node => {
            if (node.id === 'LegalReview' || node.id === 'MCOReview') {
                this.loadWidgetsForNode(node);
            }
        });

        if (!this.state.selectedNodeId) {
            const activeNode = this.selectActiveNode(details.nodeList);
            if (activeNode) {
                this.setState({
                    selectedNodeId: activeNode.id,
                    currentActiveNode: activeNode
                });
                this.loadWidgetsForNode(activeNode);
            }
        } else {
            const currentNode = details.nodeList.find(node => node.id === this.state.selectedNodeId);
            if (currentNode) {
                this.setState({
                    currentActiveNode: currentNode
                });
                this.loadWidgetsForNode(currentNode);
            }
        }
    }

    selectActiveNode(nodeList) {
        let activeNode = nodeList.find(node => node.status === 'Active');

        if (!activeNode) {
            activeNode = nodeList.find(node =>
                node.id === 'MCOReview' &&
                node.renderWidgetList &&
                node.renderWidgetList.length > 0
            );
        }

        if (!activeNode) {
            activeNode = nodeList.find(node =>
                node.id === 'LegalReview' &&
                // node.status === 'Active' &&
                node.renderWidgetList &&
                node.renderWidgetList.length > 0
            );
        }

        if (!activeNode) {
            activeNode = nodeList.find(node => node.id === 'SystemCreate');
        }

        return activeNode;
    }

    loadWidgetsForNode(node) {
        const widgetList = Widget.loadWidgetsFromNode(node);

        const editableFields = ['legalReviewDecision', 'legalReviewComment', 'mcoReviewDecision', 'mcoReviewComment'];

        const processedWidgetList = widgetList.map(widget => {
            if (!editableFields.includes(widget.id)) {
                if ((widget.id === 'supportingDocuments' || widget.id === 'resellerAuthorizationLetters') && widget.value) {
                    try {
                        let arrayData;
                        if (typeof widget.value === 'string') {
                            arrayData = JSON.parse(widget.value);
                        } else {
                            arrayData = widget.value;
                        }

                        if (Array.isArray(arrayData)) {
                            const processedArrayData = arrayData.map(item => {
                                if (Array.isArray(item)) {
                                    return item.map(subItem => {
                                        if (subItem.type === 'file' && subItem.value) {
                                            const realFileName = subItem.value.slice(36);
                                            try {
                                                const decodedFileName = decodeURIComponent(realFileName);
                                                return {
                                                    ...subItem,
                                                    originalValue: subItem.value,
                                                    value: `${subItem.value.slice(0, 36)}${decodedFileName}`
                                                };
                                            } catch (decodeError) {
                                                console.warn('Failed to decode filename:', realFileName, decodeError);
                                                return subItem;
                                            }
                                        }
                                        return subItem;
                                    });
                                }
                                return item;
                            });

                            const processedValue = typeof widget.value === 'string'
                                ? JSON.stringify(processedArrayData)
                                : processedArrayData;

                            return { ...widget, readonly: true, value: processedValue };
                        }
                    } catch (error) {
                        console.warn('Failed to process file names:', error);
                    }
                }
                return { ...widget, readonly: true };
            }
            return widget;
        });

        this.setState(prevState => {
            const existingWidgets = prevState.widgetListPerNode[node.id] || [];
            const mergedWidgetList = processedWidgetList.map(newWidget => {
                const existingWidget = existingWidgets.find(w => w.id === newWidget.id);
                if (existingWidget && existingWidget.value) {
                    return { ...newWidget, value: existingWidget.value };
                }
                return newWidget;
            });

            return {
                widgetListPerNode: {
                    ...prevState.widgetListPerNode,
                    [node.id]: mergedWidgetList
                }
            };
        });
    }

    setWidgetValue(widgetId, value) {
        const {selectedNodeId, widgetListPerNode} = this.state;
        let widgetList = widgetListPerNode[selectedNodeId];
        let widget = widgetList.find(widget => widget.id === widgetId);
        widget.value = value;
        widget.errorText = null;

        this.setState(prevState => ({
            widgetListPerNode: {
                ...prevState.widgetListPerNode,
                [selectedNodeId]: widgetList
            }
        }));
    }

    validateWidget(widgetList, decisionWidget, buttonType) {
        const shouldSkipValidation = decisionWidget && decisionWidget.value === 'reject';

        if (!shouldSkipValidation) {
            const validatedWidgets = Widget.validateWidgetValues(widgetList, buttonType);

            this.setState(prevState => ({
                widgetListPerNode: {
                    ...prevState.widgetListPerNode,
                    [this.state.selectedNodeId]: validatedWidgets
                }
            }));

            const numWidgetErrors = validatedWidgets
                .map(widget => Widget.containsError(widget))
                .reduce((prev, cur) => prev || cur);

            if (numWidgetErrors > 0) {
                console.error("Some fields failed to pass validation");
                return null;
            }

            return validatedWidgets;
        }
        return widgetList;
    }

    restructureWidget(node, widgetList) {
        return widgetList.map(widget => {
            if (widget.type === 'array') {
                const prevValues = JSON.parse(node.renderWidgetList.find(w => w.id === widget.id).value);
                if (prevValues == null) {
                    return {id: widget.id, type: widget.type, value: JSON.stringify(widget.value)};
                }
                return {
                    id: widget.id,
                    type: widget.type,
                    value: JSON.stringify(ArrayWidget.mergeWithPreviousWidgetValue(prevValues, widget.value)),
                };
            }
            return {id: widget.id, type: widget.type, value: widget.value};
        });
    }

    switchToNode(nodeId) {
        const {details} = this.props;
        if (!details || !details.nodeList) return;

        const targetNode = details.nodeList.find(node => node.id === nodeId);
        if (targetNode) {
            this.setState({
                selectedNodeId: nodeId,
                currentActiveNode: targetNode
            });
            this.loadWidgetsForNode(targetNode);
        }
    }



    handleSubmit() {
        const {currentActiveNode, widgetListPerNode, selectedNodeId} = this.state;
        const {details} = this.props;

        let widgetList = widgetListPerNode[selectedNodeId];
        
        if (selectedNodeId === 'LegalReview') {
            const legalDecisionWidget = widgetList.find(widget => widget.id === 'legalReviewDecision');
            const legalCommentWidget = widgetList.find(widget => widget.id === 'legalReviewComment');
            
            if (!legalDecisionWidget || !legalDecisionWidget.value) {
                this.setState({
                    modalVisible: true,
                    modalContent: '请选择Legal审核决策'
                });
                return;
            }
            
            if (legalDecisionWidget.value === 'reject' && (!legalCommentWidget || !legalCommentWidget.value)) {
                this.setState({
                    modalVisible: true,
                    modalContent: '拒绝时请填写审核评论'
                });
                return;
            }
            
            // 构建LegalReview的提交数据
            const legalSubmitData = [
                {
                    id: 'legalReviewDecision',
                    value: legalDecisionWidget.value,
                    type: legalDecisionWidget.type
                }
            ];
            
            if (legalCommentWidget && legalCommentWidget.value) {
                legalSubmitData.push({
                    id: 'legalReviewComment',
                    value: legalCommentWidget.value,
                    type: legalCommentWidget.type
                });
            }
            
            const status = legalDecisionWidget.value === 'approve' ? 'SUCCESS' : 'FAILURE';
            this.submitTaskStatus(details.id, details.workflowType, selectedNodeId, status, JSON.stringify(legalSubmitData));
            
        } else if (selectedNodeId === 'MCOReview') {
            const mcoDecisionWidget = widgetList.find(widget => widget.id === 'mcoReviewDecision');
            const mcoCommentWidget = widgetList.find(widget => widget.id === 'mcoReviewComment');
            
            if (!mcoDecisionWidget || !mcoDecisionWidget.value) {
                this.setState({
                    modalVisible: true,
                    modalContent: '请选择MCO审核决策'
                });
                return;
            }
            
            if (mcoDecisionWidget.value === 'reject' && (!mcoCommentWidget || !mcoCommentWidget.value)) {
                this.setState({
                    modalVisible: true,
                    modalContent: '拒绝时请填写审核评论'
                });
                return;
            }
            
            const mcoSubmitData = [
                {
                    id: 'mcoReviewDecision',
                    value: mcoDecisionWidget.value,
                    type: mcoDecisionWidget.type
                }
            ];
            
            if (mcoCommentWidget && mcoCommentWidget.value) {
                mcoSubmitData.push({
                    id: 'mcoReviewComment',
                    value: mcoCommentWidget.value,
                    type: mcoCommentWidget.type
                });
            }
            
            const status = mcoDecisionWidget.value === 'approve' ? 'SUCCESS' : 'FAILURE';
            this.submitTaskStatus(details.id, details.workflowType, selectedNodeId, status, JSON.stringify(mcoSubmitData));
            
        } else {
            const decisionWidget = widgetList.find(widget => widget.id.includes('Decision'));
            widgetList = this.validateWidget(widgetList, decisionWidget, "Submit");
            if (!widgetList) return;

            let mergedWidgetList = this.restructureWidget(currentActiveNode, widgetList);
            const status = !decisionWidget || decisionWidget.value === 'approve' ? 'SUCCESS' : 'FAILURE';
            this.submitTaskStatus(details.id, details.workflowType, selectedNodeId, status, JSON.stringify(mergedWidgetList));
        }
    }

    submitTaskStatus(executionId, workflowType, nodeId, status, inputJson) {
        this.setState(prevState => ({
            isSubmitNodes: {
                ...prevState.isSubmitNodes,
                [nodeId]: true
            }
        }));

        this.client.then((result) => {
            result.sendTaskStatus(executionId, workflowType, nodeId, status, inputJson)
                .then(result => {
                    console.log("SendTaskStatus OK:", result);
                    this.props.setSuccessMsg(nodeId, SUBMIT_SUCCESS_TIP);
                    
                    this.setState(prevState => ({
                        submittedNodes: {
                            ...prevState.submittedNodes,
                            [nodeId]: true
                        }
                    }));
                    
                    setTimeout(() => {
                        PubSub.publish('updateWorkflow');
                    }, 1000); // 延迟1秒更新，让用户看到成功提示
                })
                .catch(err => {
                    console.log("SendTaskStatus ERR:", err);
                    this.props.setAlertMsg(err);
                })
                .finally(() => {
                    this.setState(prevState => ({
                        isSubmitNodes: {
                            ...prevState.isSubmitNodes,
                            [nodeId]: false
                        }
                    }));
                });
        });
    }

    checkReviewRejection() {
        const {details} = this.props;
        const {widgetListPerNode} = this.state;
        
        if (!details || !details.nodeList) return null;
        
        const rejectionInfo = {
            legalRejected: false,
            legalReason: '',
            mcoRejected: false,
            mcoReason: ''
        };
        
        const legalNode = details.nodeList.find(node => node.id === 'LegalReview');
        if (legalNode && widgetListPerNode['LegalReview']) {
            const legalWidgets = widgetListPerNode['LegalReview'];
            const legalDecision = legalWidgets.find(widget => widget.id === 'legalReviewDecision');
            const legalComment = legalWidgets.find(widget => widget.id === 'legalReviewComment');
            
            if (legalDecision && legalDecision.value === 'reject') {
                rejectionInfo.legalRejected = true;
                rejectionInfo.legalReason = legalComment && legalComment.value ? legalComment.value : '无拒绝原因';
            }
        }
        
        const mcoNode = details.nodeList.find(node => node.id === 'MCOReview');
        if (mcoNode && widgetListPerNode['MCOReview']) {
            const mcoWidgets = widgetListPerNode['MCOReview'];
            const mcoDecision = mcoWidgets.find(widget => widget.id === 'mcoReviewDecision');
            const mcoComment = mcoWidgets.find(widget => widget.id === 'mcoReviewComment');
            
            if (mcoDecision && mcoDecision.value === 'reject') {
                rejectionInfo.mcoRejected = true;
                rejectionInfo.mcoReason = mcoComment && mcoComment.value ? mcoComment.value : '无拒绝原因';
            }
        }
        
        return rejectionInfo;
    }

    renderStatusAlert() {
        const {workflowStatus, selectedNodeId} = this.state;
        const {details} = this.props;
        let statusText = '';
        let statusType = 'info';
        let alertContent = '';
        
        switch (workflowStatus) {
            case 'RUNNING':
                if (selectedNodeId === 'LegalReview') {
                    statusText = '等待Legal审核';
                } else if (selectedNodeId === 'MCOReview') {
                    statusText = '等待MCO审核';
                } else {
                    statusText = '处理中';
                }
                statusType = 'info';
                break;
            case 'Rollback':
                statusText = '审核拒绝';
                statusType = 'warning';
                break;
            case 'SUCCEEDED':
                statusText = '流程已完成';
                statusType = 'success';
                
                const rejectionInfo = this.checkReviewRejection();
                if (rejectionInfo && (rejectionInfo.legalRejected || rejectionInfo.mcoRejected)) {
                    statusText = '审核拒绝';
                    statusType = 'warning';
                    
                    const rejectionReasons = [];
                    if (rejectionInfo.legalRejected) {
                        rejectionReasons.push(`Legal审核拒绝：${rejectionInfo.legalReason}`);
                    }
                    if (rejectionInfo.mcoRejected) {
                        rejectionReasons.push(`MCO审核拒绝：${rejectionInfo.mcoReason}`);
                    }
                    
                    alertContent = rejectionReasons.join('；');
                }
                break;
            case 'FAILED':
                statusText = '处理失败';
                statusType = 'error';
                break;
            default:
                statusText = '未知状态';
                statusType = 'warning';
        }

        return (
            <Alert
                visible={true}
                type={statusType}
                header={`工作流状态: ${statusText}`}
            >
                {alertContent && <div>{alertContent}</div>}
            </Alert>
        );
    }

    renderScenarioAlert() {
        const {workflowStatus, selectedNodeId, widgetListPerNode} = this.state;
        const {details} = this.props;
        
        let scenarioValue = null;
        let listingIdUrlValue = null;
        
        if (selectedNodeId && widgetListPerNode[selectedNodeId]) {
            const currentWidgetList = widgetListPerNode[selectedNodeId];
            const scenarioWidget = currentWidgetList.find(widget => widget.id === 'scenario');
            const listingIdUrlWidget = currentWidgetList.find(widget => widget.id === 'listingIdUrl');
            
            scenarioValue = scenarioWidget?.value;
            listingIdUrlValue = listingIdUrlWidget?.value;
        }
        
        console.log('Scenario debug:', {
            workflowStatus,
            selectedNodeId,
            scenarioValue,
            listingIdUrlValue,
            widgetCount: widgetListPerNode[selectedNodeId]?.length || 0
        });
        
        if (workflowStatus !== 'RUNNING' || !scenarioValue) {
            return null;
        }

        let scenarioMessage = '';
        let scenarioContent = null;

        switch (scenarioValue) {
            case 'UpdateVisibility':
            case 'updateVisibility':
                scenarioMessage = '当前seller将limited产品发布为public，可以登录mco账号（716746580080），去PDP页面直接查看';
                if (listingIdUrlValue) {
                    scenarioContent = (
                        <div>
                            {scenarioMessage}
                            <div style={{marginTop: '8px'}}>
                                <a href={listingIdUrlValue} target="_blank" rel="noopener noreferrer">
                                    查看PDP页面
                                </a>
                            </div>
                        </div>
                    );
                } else {
                    scenarioContent = scenarioMessage;
                }
                break;
            case 'UpdateInformation':
            case 'updateInformation':
                scenarioMessage = '当前seller在更新产品信息，请重点review产品内容相关字段';
                scenarioContent = scenarioMessage;
                break;
            case 'UpdateDeliveryOptions':
            case 'updateDeliveryOptions':
                scenarioMessage = '当前seller在更新fulfillmentUrl，请重点关注fulfillmentUrl字段';
                scenarioContent = scenarioMessage;
                break;
            default:
                return null;
        }

        return (
            <Alert
                visible={true}
                type="info"
                header="审核提示"
            >
                {scenarioContent}
            </Alert>
        );
    }

    renderCustomWidget(widget) {
        if (!widget) {
            return null;
        }

        const { id, label, value, readonly } = widget;
        
        if (!value || value === '' || value === null || value === undefined) {
            return null;
        }

        const safeValue = String(value);

        const renderValue = () => {
            try {
                switch (id) {
                    case 'listingIdUrl':
                        return (
                            <a href={safeValue} target="_blank" rel="noopener noreferrer">
                                {safeValue}
                            </a>
                        );
                    case 'logoUrl':
                        return (
                            <a href={safeValue} target="_blank" rel="noopener noreferrer">
                                {safeValue}
                            </a>
                        );
                    case 'videoUrl':
                        return (
                            <a href={safeValue} target="_blank" rel="noopener noreferrer">
                                {safeValue}
                            </a>
                        );
                    case 'promotionalMedia':
                        try {
                            const media = JSON.parse(safeValue);
                            if (Array.isArray(media) && media.length > 0) {
                                return (
                                    <div>
                                        {media.map((item, index) => (
                                            <div key={index} style={{
                                                marginBottom: index < media.length - 1 ? '12px' : 0,
                                                paddingBottom: index < media.length - 1 ? '12px' : 0,
                                                borderBottom: index < media.length - 1 ? '1px dashed #e1e1e1' : 'none'
                                            }}>
                                                <div><strong>Item {index + 1}</strong></div>
                                                <div><strong>Title:</strong> {item.Title || '-'}</div>
                                                <div><strong>Type:</strong> {item.Type || '-'}</div>
                                                <div><strong>Description:</strong> {item.Description || '-'}</div>
                                                <div style={{ wordBreak: 'break-all' }}>
                                                    <strong>Url:</strong>{' '}
                                                    {item.Url ? (
                                                        <a href={item.Url} target="_blank" rel="noopener noreferrer">
                                                            {item.Url}
                                                        </a>
                                                    ) : '-'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            }
                        } catch (e) {
                            console.warn('Failed to parse promotionalMedia JSON:', e);
                        }
                        return <div style={{ wordBreak: 'break-word' }}>{safeValue}</div>;
                    case 'highlights':
                        try {
                            const highlights = JSON.parse(safeValue);
                            if (Array.isArray(highlights)) {
                                return (
                                    <div>
                                        {highlights.slice(0, 3).map((highlight, index) => (
                                            <div key={index} style={{ 
                                                marginBottom: '8px', 
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}>
                                                • {String(highlight || '')}
                                            </div>
                                        ))}
                                    </div>
                                );
                            }
                        } catch (e) {
                            console.warn('Failed to parse highlights JSON:', e);
                        }
                        return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{safeValue}</div>;
                    case 'categories':
                        try {
                            const categories = JSON.parse(safeValue);
                            if (Array.isArray(categories)) {
                                return (
                                    <div>
                                        {categories.map((category, index) => (
                                            <span key={index}>
                                                {String(category || '')}
                                                {index < categories.length - 1 ? ', ' : ''}
                                            </span>
                                        ))}
                                    </div>
                                );
                            }
                        } catch (e) {
                            console.warn('Failed to parse categories JSON:', e);
                        }
                        return <div>{safeValue}</div>;
                    case 'shortDescription':
                    case 'longDescription':
                    case 'supportDetail':
                    case 'refundPolicy':
                        return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{safeValue}</div>;
                    case 'taxRate':
                    case 'taxRateDesc':
                        return <div style={{ wordBreak: 'break-word' }}>{safeValue}</div>;
                    default:
                        return <div style={{ wordBreak: 'break-word' }}>{safeValue}</div>;
                }
            } catch (error) {
                console.error('Error rendering widget value for', id, ':', error);
                return <div style={{ wordBreak: 'break-word' }}>{safeValue}</div>;
            }
        };

        return (
            <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                    fontWeight: 'bold', 
                    marginBottom: '4px',
                    fontSize: '14px'
                }}>
                    {label || id || 'Unknown Field'}
                </div>
                <div style={{ 
                    padding: '8px 12px',
                    backgroundColor: '#fafafa',
                    border: '1px solid #e1e1e1',
                    borderRadius: '4px',
                    fontSize: '14px',
                    lineHeight: '1.5'
                }}>
                    {renderValue()}
                </div>
            </div>
        );
    }

    renderWidgetGroup(title, fieldIds, widgetList) {
        if (!widgetList) return null;

        const groupWidgets = widgetList.filter(widget => fieldIds.includes(widget.id));
        if (groupWidgets.length === 0) return null;

        const isProductInfo = fieldIds === productInfoFields;
        const isReviewMaterials = fieldIds === reviewMaterialsFields;

        const renderWidgets = () => {
            return groupWidgets.map((widget, index) => {
                // 如果是文件类型或有特定的widget类型需要特殊处理，使用原始Widget渲染
                if (widget.type === 'file' || widget.type === 'array' || 
                    widget.id === 'supportingDocuments' || widget.id === 'resellerAuthorizationLetters' ||
                    (!isProductInfo && !isReviewMaterials)) {
                    return (
                        <div key={widget.id || index}>
                            {Widget.getWidgetByType(widget, this.setWidgetValue)}
                        </div>
                    );
                }
                
                if ((isProductInfo || isReviewMaterials) && widget.readonly) {
                    const customWidget = this.renderCustomWidget(widget);
                    return customWidget ? (
                        <div key={widget.id || index}>
                            {customWidget}
                        </div>
                    ) : null;
                }
                
                return (
                    <div key={widget.id || index}>
                        {Widget.getWidgetByType(widget, this.setWidgetValue)}
                    </div>
                );
            }).filter(Boolean);
        };

        return title !== "" ? (
            <Container header={<Header variant="h3">{title}</Header>}>
                <SpaceBetween direction="vertical" size="m">
                    {renderWidgets()}
                </SpaceBetween>
            </Container>
        ) : (
            <SpaceBetween direction="vertical" size="m">
                {renderWidgets()}
            </SpaceBetween>
        );
    }

    renderActionButtons() {
        const {currentActiveNode, isSubmitNodes, selectedNodeId, workflowStatus, submittedNodes} = this.state;

        if (!currentActiveNode || !currentActiveNode.editable ||
            workflowStatus === 'SUCCEEDED' || workflowStatus === 'FAILED' || workflowStatus === 'Rollback' ||
            submittedNodes[selectedNodeId]) {
            return null;
        }

        return (
            <SpaceBetween direction="horizontal" size="xs">
                <Button
                    loading={isSubmitNodes[selectedNodeId]}
                    variant="primary"
                    onClick={this.handleSubmit}
                >
                    Submit
                </Button>
            </SpaceBetween>
        );
    }

    renderLegalReviewSection(widgetList) {
        const {selectedNodeId} = this.state;
        const isLegalReviewActive = selectedNodeId === 'LegalReview';

        return (
            <Container header={
                <Header variant="h3">
                    Legal审核
                </Header>
            }>
                <SpaceBetween direction="vertical" size="m">
                    {this.renderWidgetGroup("", legalReviewFields, widgetList)}
                    {isLegalReviewActive && this.renderActionButtons()}
                </SpaceBetween>
            </Container>
        );
    }

    renderMCOReviewSection(widgetList) {
        const {selectedNodeId} = this.state;
        const isMCOReviewActive = selectedNodeId === 'MCOReview';

        return (
            <Container header={
                <Header variant="h3">
                    MCO审核
                </Header>
            }>
                <SpaceBetween direction="vertical" size="m">
                    {this.renderWidgetGroup("", mcoReviewFields, widgetList)}
                    {isMCOReviewActive && this.renderActionButtons()}
                </SpaceBetween>
            </Container>
        );
    }

    renderModal() {
        const {modalVisible, modalContent} = this.state;
        
        return (
            <Modal
                visible={modalVisible}
                onDismiss={() => this.setState({modalVisible: false})}
                header="提示"
                size="medium"
            >
                <SpaceBetween direction="vertical" size="m">
                    <div>{modalContent}</div>
                    <Button
                        variant="primary"
                        onClick={() => this.setState({modalVisible: false})}
                    >
                        确定
                    </Button>
                </SpaceBetween>
            </Modal>
        );
    }

    render() {
        const {details} = this.props;
        const {selectedNodeId, widgetListPerNode, currentActiveNode} = this.state;

        if (!details || !selectedNodeId) {
            return (
                <Container header={<Header variant="h2">SSLV2 Product Review</Header>}>
                    <Alert visible={true} type="info" header="无数据">
                        暂无工作流数据可显示
                    </Alert>
                </Container>
            );
        }

        const widgetList = widgetListPerNode[selectedNodeId];

        return (
            <Container header={<Header variant="h2">SSLV2 Product Review</Header>}>
                <SpaceBetween direction="vertical" size="l">
                    {this.renderStatusAlert()}
                    {this.renderScenarioAlert()}
                    
                    <ColumnLayout columns={1}>
                        {this.renderWidgetGroup("产品信息", productInfoFields, widgetList)}
                        {this.renderWidgetGroup("审核材料", reviewMaterialsFields, widgetList)}
                        {this.renderLegalReviewSection(widgetList)}
                        {this.renderMCOReviewSection(widgetList)}
                    </ColumnLayout>
                </SpaceBetween>
                {this.renderModal()}
            </Container>
        );
    }
}